/**
 * allergySort — the ONE ordering for every allergy list on the record:
 * most severe first, then alphabetical by substance. Shared by the
 * CriticalStrip, the Care record's AllergiesCard (cardVariants.tsx), the
 * Face Sheet (faceSheet.ts), and the Medication List's allergies line
 * (medList.ts) — one sort, everywhere an allergy list renders, so the
 * order a CM sees on the record is the order that prints and the order
 * that lands in a downloaded document.
 *
 * A severity nobody has stated — the field left empty, or the honest
 * 'unknown' value — carries no ranking information, so both sort into
 * the same last tier and neither renders the inline marker
 * (allergySeverityMarker): "I don't know" and "nobody asked" read the
 * same to a reader deciding what to worry about first.
 */

import type { AllergyRow, AllergySeverity } from '@/lib/clientRecord';

const SEVERITY_RANK: Record<AllergySeverity, number> = {
  severe: 0,
  moderate: 1,
  mild: 2,
  unknown: 3,
};

/** Rank of an unstated severity mirrors the explicit 'unknown' value —
 *  both carry the same (lack of) information. */
function severityRank(severity: AllergySeverity | undefined): number {
  return severity === undefined ? SEVERITY_RANK.unknown : SEVERITY_RANK[severity];
}

/**
 * Severity first (severe, moderate, mild, then unstated/unknown), then
 * alphabetical by substance (case-insensitive) within a tier. Returns a
 * NEW array — the input is never mutated, so a caller holding the wire/
 * creation-order array elsewhere (the archived-reveal's entry-order
 * block, for one) is unaffected.
 */
export function sortAllergiesBySeverity<T extends Pick<AllergyRow, 'substance' | 'severity'>>(
  rows: readonly T[],
): T[] {
  return [...rows].sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity);
    if (bySeverity !== 0) return bySeverity;
    return a.substance.localeCompare(b.substance, undefined, { sensitivity: 'base' });
  });
}

/** The lowercase prose word for a STATED severity — "Penicillin ·
 *  severe". Shared verbatim by the strip's inline marker and the
 *  Medication List's allergy line (medList.ts), the two surfaces that
 *  render severity as plain prose rather than a labeled pill. */
export const ALLERGY_SEVERITY_WORD: Record<Exclude<AllergySeverity, 'unknown'>, string> = {
  severe: 'severe',
  moderate: 'moderate',
  mild: 'mild',
};

/**
 * The inline marker text for one allergy's severity, or null when
 * there's nothing worth flagging — no severity stated, or the honest
 * 'unknown' value (mirrors the Care record card's collapsed summary,
 * which shows a severity word only when one has actually been stated).
 */
export function allergySeverityMarker(severity: AllergySeverity | undefined): string | null {
  if (severity === undefined || severity === 'unknown') return null;
  return ALLERGY_SEVERITY_WORD[severity];
}
